import {ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild} from '@angular/core';
import { TeacherPortfolioRecordTypeEnum, TeacherActivity, TeacherActivityService, TeacherFile, TeacherFilesService } from "@isophi/pap-api";
import { getFileDisplayName, RenameFileModalComponent, ToastService } from '@isophi/core-ui';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoService } from '@jsverse/transloco';
import { takeUntil } from 'rxjs';
import { BaseComponent } from '../base-component/base.component';

@Component({
    selector: 'app-portfolio-plan-photos',
    templateUrl: './portfolio-plan-photos.component.html',
    styleUrls: ['./portfolio-plan-photos.component.scss'],
    standalone: false
})
export class PortfolioPlanPhotosComponent extends BaseComponent {
  @Input() teacherActivityId: string;
  @Input() photos: TeacherFile[] = [];

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  @Output() photoCreated = new EventEmitter<TeacherActivity>();
  @Output() photoDeleted = new EventEmitter<string>();
  @Output() photoRenamed = new EventEmitter<TeacherFile>();

  protected readonly recordTypeEnum = TeacherPortfolioRecordTypeEnum;

  private readonly teacherActivityService = inject(TeacherActivityService);
  private readonly teacherFilesService = inject(TeacherFilesService);
  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly modalService = inject(NgbModal);

  protected readonly getFileDisplayName = getFileDisplayName;

  getBackgroundImageUrl(photo: TeacherFile): string {
    return `url(${photo.file})`;
  }

  private createTeacherActivityPhoto(file: File): void {
    this.teacherActivityService
      .teacherActivityPhotoCreateForm(this.teacherActivityId, file, file.name)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (teacherActivity) => {
          this.photos = teacherActivity.photos;
          this.photoCreated.emit(teacherActivity);
          this.toastService.success(this.translocoService.translate('portfolio/photoCreateSuccessToast'));
        },
        error: (err) => {
          this.toastService.error(this.translocoService.translate('portfolio/photoCreateErrorToast'));
        },
      });
  }

  private deleteTeacherActivityPhoto(photoId: string): void {
    this.teacherFilesService
      .teacherFilesDestroy(photoId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: () => {
          this.photos = this.photos.filter((photo) => photo.id !== photoId);
          this.toastService.success(this.translocoService.translate('portfolio/photoDeleteSuccessToast'));
          this.cd.markForCheck();
        },
        error: (err) => {
          this.toastService.error(this.translocoService.translate('portfolio/photoDeleteErrorToast'));
        },
      });
  }

  private renameTeacherActivityPhoto(photo: TeacherFile, title: string): void {
    this.teacherFilesService
      .teacherFilesPartialUpdate(photo.id, { title })
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (updatedPhoto) => {
          this.photos = this.photos.map((p) => (p.id === updatedPhoto.id ? updatedPhoto : p));
          this.photoRenamed.emit(updatedPhoto);
          this.toastService.success(this.translocoService.translate('portfolio/fileRenameSuccessToast'));
          this.cd.markForCheck();
        },
        error: () => {
          this.toastService.error(this.translocoService.translate('portfolio/fileRenameErrorToast'));
        },
      });
  }

  openFileInput(): void {
    this.fileInputRef.nativeElement.click();
  }

  handleFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    this.createTeacherActivityPhoto(file);
  }

  async handleRenamePhoto(photo: TeacherFile, event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    const title = await RenameFileModalComponent.open(this.modalService, photo);
    if (title !== null && title !== photo.title) {
      this.renameTeacherActivityPhoto(photo, title);
    }
  }

  handleDeletePhoto(photoId: string, event: Event): void {
    event.preventDefault();
    this.deleteTeacherActivityPhoto(photoId);
  }
}
